<script setup lang="ts">
import { computed, ref } from 'vue'
import { VButton, VIcon } from '@/modules/shared/components'
import { autoLink } from '@/modules/investing/utils/auto-link'
import NoteForm from './note-form.vue'
import type { Note } from '@/modules/investing/stores/note-store'

const props = defineProps<{
  note: Note
  commentableType: string
  commentableId: string
  permission?: string
  allowReplies: boolean
  depth: number
  currentUserId: number
  isGroupAdmin: boolean
  onUpdate: (noteId: number, payload: { text: string }) => Promise<void>
  onDelete: (noteId: number) => Promise<void>
  onReply: (payload: { text: string; permission?: string }) => Promise<void>
}>()

const editing = ref(false)
const replying = ref(false)

const canModify = computed(() => {
  return props.note.author_id === props.currentUserId || props.isGroupAdmin
})

const formattedDate = computed(() => {
  const date = new Date(props.note.created_at)
  const now = new Date()
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[date.getMonth()]
  const day = date.getDate()

  if (date.getFullYear() !== now.getFullYear()) {
    return `${month} ${day}, ${date.getFullYear()}`
  }
  return `${month} ${day}`
})

const linkedText = computed(() => autoLink(props.note.text))

const handleUpdate = async (payload: { text: string }) => {
  await props.onUpdate(props.note.id, payload)
  editing.value = false
}

const handleDelete = async () => {
  if (!window.confirm('Are you sure you want to delete this note?')) return
  await props.onDelete(props.note.id)
}

const handleReply = async (payload: { text: string; permission?: string }) => {
  await props.onReply(payload)
  replying.value = false
}
</script>

<template>
  <div :class="['rounded-lg border border-gray-200 bg-white', depth > 0 ? 'ml-8' : '']">
    <div class="p-4">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2 text-sm text-gray-500">
          <span class="font-medium text-gray-900">{{ note.author_name }}</span>
          <span>&middot;</span>
          <span>{{ formattedDate }}</span>
          <span v-if="note.is_edited" class="italic">(edited)</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="text-xs text-gray-400">Visible to {{ note.permission_label }}</span>
          <template v-if="canModify">
            <VButton :click="() => (editing = !editing)" size="xs" variant="v-white">
              {{ editing ? 'Cancel' : 'Edit' }}
            </VButton>
            <VButton :click="handleDelete" size="xs" variant="v-white"> Delete </VButton>
          </template>
        </div>
      </div>

      <!-- Body -->
      <div v-if="editing" class="mt-3">
        <NoteForm
          mode="edit"
          :initialText="note.text"
          :permission="note.permission"
          :onSubmit="handleUpdate"
          :onCancel="() => (editing = false)"
        />
      </div>
      <div v-else class="mt-2 whitespace-pre-wrap text-sm text-gray-700" v-html="linkedText" />

      <!-- Reply button -->
      <div v-if="allowReplies && depth === 0 && !editing" class="mt-2">
        <button class="text-sm text-sky-600 hover:text-sky-700" @click="replying = !replying">
          {{ replying ? 'Cancel reply' : 'Reply' }}
        </button>
      </div>
    </div>

    <!-- Reply form -->
    <div v-if="replying" class="border-t border-gray-100 p-4">
      <NoteForm
        mode="reply"
        :permission="permission || note.permission"
        :onSubmit="handleReply"
        :onCancel="() => (replying = false)"
      />
    </div>

    <!-- Replies -->
    <div v-if="note.replies?.length && depth === 0" class="border-t border-gray-100 p-4">
      <NoteItem
        v-for="reply in note.replies"
        :key="reply.id"
        :note="reply"
        :commentableType="commentableType"
        :commentableId="commentableId"
        :permission="permission"
        :allowReplies="false"
        :depth="depth + 1"
        :currentUserId="currentUserId"
        :isGroupAdmin="isGroupAdmin"
        :onUpdate="onUpdate"
        :onDelete="onDelete"
        :onReply="onReply"
        class="mb-2 last:mb-0"
      />
    </div>
  </div>
</template>
